import React, { useContext, useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import { TCart, TCartType, TProductCart } from '@com/core/common/types/cart';
import { cartSVC as Cart } from '@com/core/common/services/api';
import { reportError } from '@com/core/common/utils/sentry';
import { AppContext } from '@com/core/common/contexts/appContext';
import { NavContext } from '@com/core/common/contexts/navContext';
import { TCity } from '@com/core/common/types/locale';
import Analytics from '@com/core/common/utils/analytics';
import { toUSD } from '@com/core/common/utils/currency';
import { animateScroll as scroll } from 'react-scroll';
import { useRouter } from 'next/router';
import { APP } from '../baseConfig';
import { TBannerModule, TFbtOffer } from '../types/catalog';

type TProps = {
    children: JSX.Element;
};

type TLoyaltyCMSType = {
    top?: TBannerModule;
    midHigh?: TBannerModule;
    midLow?: TBannerModule;
    bottom?: TBannerModule;
};

export type TLifeCycle = 'init' | 'loading' | 'error' | 'success';

export type TCartContextFunctions = {
    addAttachmentsToCart: (offerCode: string, attachments: string[]) => void;
    addAddonsToCart: (offerCode: string, fbtItems?: TFbtOffer[]) => void;
    addToCart: (
        offerCode: string,
        quantity: number,
        showModal: boolean,
        attachments?: string[],
        sku?: string,
        isFouponSelected?: boolean,
    ) => void;
    addToWishlist: (offerCode: string, sku: string) => void;
    isInWishlist: (offerCode: string) => boolean;
    applyCoupon: (code: string) => void;
    deleteCoupon: () => void;
    getCart: (dismissWarnings: boolean) => void;
    moveItem: (offerId: string, cartType: TCartType, sku: string) => void;
    removeItem: (offerId: string, cartType: TCartType, sku: string) => void;
    updateQuantity: (offerId: string, quantity: number) => void;
    setDestination: (cityId: number) => void;
    setIsCartSucceeded: (isSucceeded: boolean) => void;
    getSelectedDestination: () => TCity;
    closeQuickCart: () => void;
    isAdded: (offerCode: string) => boolean;
    getItemQty: (offerCode: string) => number;
    getItemMaxQty: (offerCode: string) => number;
    setFoupon: (offerCode: string, isFouponSelected: boolean) => void;
    setAddonToCartState: (state: TLifeCycle) => void;
    setAddToCartState: (state: TLifeCycle) => void;
};

type TCartContextState = {
    cart: TCart | null;
    isAddToCartLoading: boolean;
    isCartLoading: boolean;
    isCartSucceeded: boolean;
    isDiscountLoading: boolean;
    isQuickCartOpen: boolean;
    isCityLoading: boolean;
    addonToCartState: TLifeCycle;
    addToCartState: TLifeCycle;
    isAddonToCartLoading: boolean;
    cartError: AxiosError | null;
    cmsData: TLoyaltyCMSType;
};

const initContextState: TCartContextState & TCartContextFunctions = {
    cart: null,
    cartError: null,
    isAddToCartLoading: false,
    isCartLoading: true,
    isCartSucceeded: false,
    addonToCartState: 'init' as TLifeCycle,
    addToCartState: 'init' as TLifeCycle,
    isAddonToCartLoading: false,
    isCityLoading: false,
    isDiscountLoading: false,
    isQuickCartOpen: false,
    cmsData: {},
    addAttachmentsToCart: async (): Promise<void> => undefined,
    addAddonsToCart: async (): Promise<void> => undefined,
    addToCart: async (): Promise<void> => undefined,
    addToWishlist: async (): Promise<void> => undefined,
    isInWishlist: (): boolean => false,
    applyCoupon: async (): Promise<void> => undefined,
    deleteCoupon: async (): Promise<void> => undefined,
    getCart: async (): Promise<void> => undefined,
    moveItem: async (): Promise<void> => undefined,
    removeItem: async (): Promise<void> => undefined,
    updateQuantity: async (): Promise<void> => undefined,
    setDestination: async (): Promise<void> => undefined,
    getSelectedDestination: (): TCity => ({} as TCity),
    setIsCartSucceeded: (): void => undefined,
    closeQuickCart: () => undefined,
    isAdded: (): boolean => false,
    getItemQty: (): number => 0,
    getItemMaxQty: (): number => 0,
    setFoupon: async (): Promise<void> => undefined,
    setAddonToCartState: (): void => null,
    setAddToCartState: (): void => null,
};

// eslint-disable-next-linevariable-name
export const CartContext = React.createContext(initContextState);

// eslint-disable-next-linevariable-name
export const CartContextProvider = ({ children }: TProps): React.ReactElement => {
    const [cart, setCart] = useState<TCart | null>(null);
    const [cartError, setCartError] = useState<AxiosError | null>(null);
    const [isCartLoading, setIsCartLoading] = useState<boolean>(true);
    const [addonToCartState, setAddonToCartState] = useState<TLifeCycle>('init');
    const [addToCartState, setAddToCartState] = useState<TLifeCycle>('init');
    const [isAddToCartLoading, setIsAddToCartLoading] = useState(false); /* TODO: Need to remove this */
    const [isCartSucceeded, setIsCartSucceeded] = useState<boolean>(false); /* TODO: Need to remove this */
    const [isCityLoading, setIsCityLoading] = useState<boolean>(false);
    const [isDiscountLoading, setIsDiscountLoading] = useState<boolean>(false);
    const [isQuickCartOpen, setIsQuickCartOpen] = useState<boolean>(false);
    const [isAddonToCartLoading, setIsAddonToCartLoading] = useState(false);

    const { locale, webConfig } = useContext(AppContext);
    const { isLoading: isPageLoading } = useContext(NavContext);

    // if page is loading, force close quick cart
    useEffect(() => {
        setIsQuickCartOpen(false);
    }, [isPageLoading]);

    const router = useRouter();

    const getCart = async (dismissWarnings = false): Promise<void> => {
        setIsCartLoading(true);
        try {
            const response = await Cart(locale.apiLocale).getCart(dismissWarnings);
            setCart(response.data);
            setIsCartLoading(false);
        } catch (error) {
            setIsCartLoading(false);
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const isCart = router?.route === '/cart';

    useEffect(() => {
        if (APP !== 'ofd') {
            getCart();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCart]);

    const addAttachmentsToCart = async (offerCode: string, attachments: string[]): Promise<void> => {
        try {
            const response = await Cart(locale.apiLocale).postAttachments(offerCode, attachments);
            setCart(response.data);
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.addAttachmentsToCart' });
        }
    };

    // Only for FBT things!!!!
    const fbtTracking = (fbtCart: TFbtOffer[], updatedCart: TCart): void => {
        fbtCart.map(fbtItem => {
            return Analytics.trackAddToCart(locale, {
                event: 'add_to_cart',
                eventAction: fbtItem.offer_code,
                eventLabel: fbtItem.sku,
                eventLocation: 'page_detail_fbt',
                offerId: fbtItem.offer_code,
                quantity: 1,
                cartItemsTotal: updatedCart.cartCount || 0,
                cartValueTotal: toUSD(locale.isoLocale, updatedCart.invoice.total || 0),
            });
        });
    };

    const addAddonsToCart = async (offerCodes: string, fbtItem?: TFbtOffer[]): Promise<void> => {
        setIsAddonToCartLoading(true);
        setAddonToCartState('loading');
        try {
            const response = await Cart(locale.apiLocale).postFbtItems(offerCodes);
            const updatedCart = response && response.data;
            setCart(response.data);
            fbtTracking(fbtItem, updatedCart);
            setIsAddonToCartLoading(false);
            setAddonToCartState('success');
        } catch (error) {
            setCartError(error);
            setIsAddonToCartLoading(false);
            reportError(error, { componentStack: 'cartContext.addAddonsToCart' });
            setAddonToCartState('error');
        }
    };

    const addToCart = async (
        offerCode: string,
        quantity: number,
        showModal: boolean,
        attachments?: string[],
        sku?: string,
        isFouponSelected?: boolean,
    ): Promise<void> => {
        setIsAddToCartLoading(true); /* TODO: Need to remove */
        setIsCartSucceeded(false); /* TODO: Need to remove */
        setAddToCartState('loading');
        try {
            const response = await Cart(locale.apiLocale).postToCart(
                offerCode,
                { quantity, attachments },
                isFouponSelected,
            );
            setCart({ ...cart, ...response.data } as TCart);
            const updatedCart = response && response.data;
            setIsQuickCartOpen(showModal);
            setIsAddToCartLoading(false); /* TODO: Need to remove */
            setIsCartSucceeded(true); /* TODO: Need to remove */
            setAddToCartState('success');
            Analytics.trackAddToCart(locale, {
                event: 'add_to_cart',
                eventAction: offerCode,
                eventLabel: sku,
                offerId: offerCode,
                quantity,
                cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.total) || 0),
            });
        } catch (error) {
            setAddToCartState('error');
            setIsAddToCartLoading(false); /* TODO: Need to remove */
            setIsCartSucceeded(false); /* TODO: Need to remove */
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.addToCart' });
        }
    };

    const closeQuickCart = (): void => {
        setIsQuickCartOpen(false);
    };

    const addToWishlist = async (offerCode: string, sku: string): Promise<void> => {
        try {
            const response = await Cart(locale.apiLocale).postSavedCart(offerCode);
            setCart(response.data);
            Analytics.trackAddToCart(locale, {
                event: 'add_to_savedcart',
                eventAction: offerCode,
                eventLabel: sku,
                offerId: offerCode,
                quantity: 1,
            });
        } catch (error) {
            setCartError(error);

            reportError(error, { componentStack: 'cartContext.addToWishlist' });
        }
    };

    const isInWishlist = (offerCode: string): boolean => {
        if (cart) {
            return !!cart.savedcart.find(item => {
                return 'offerCode' in item && item.offerCode === offerCode;
            });
        }
        return false;
    };

    const applyCoupon = async (code: string): Promise<void> => {
        setIsDiscountLoading(true);

        try {
            const response = await Cart(locale.apiLocale).postCoupon(code);
            setCart({ ...cart, ...response.data });
            setIsDiscountLoading(false);
        } catch (error) {
            setIsDiscountLoading(false);

            reportError(error, { componentStack: 'cartContext.applyCoupon' });
        }
    };

    const deleteCoupon = async (): Promise<void> => {
        setIsDiscountLoading(true);

        try {
            const response = await Cart(locale.apiLocale).deleteCoupon();
            setCart({ ...cart, ...response.data });
            setIsDiscountLoading(false);
        } catch (error) {
            setIsDiscountLoading(false);

            reportError(error, { componentStack: 'cartContext.deleteCoupon' });
        }
    };

    const moveItem = async (offerId: string, cartType: TCartType, sku: string): Promise<void> => {
        setIsCartLoading(true);

        try {
            const response = await Cart(locale.apiLocale).postMove(locale.isoLocale, offerId, cartType, sku);
            setCart(response.data);
            setIsCartLoading(false);
            const updatedCart = response && response.data;
            Analytics.trackAddToCart(locale, {
                event: cartType === 'savedcart' ? 'add_to_savedcart' : 'add_to_cart',
                eventAction: offerId,
                eventLabel: sku,
                offerId,
                skuConfig: sku,
                cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.total) || 0),
            });

            Analytics.trackRemoveFromCart(locale, {
                event: cartType === 'savedcart' ? 'remove_from_cart' : 'remove_from_savedcart',
                eventAction: offerId,
                eventLabel: sku,
                offerId,
                skuConfig: sku,
                cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.total) || 0),
            });
        } catch (error) {
            setIsCartLoading(false);
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const removeItem = async (offerId: string, cartType: TCartType, sku: string): Promise<void> => {
        setIsCartLoading(true);
        try {
            const response = await Cart(locale.apiLocale).remove(offerId, cartType);
            const updatedCart = response && response.data;
            setCart(response.data);
            setIsCartLoading(false);
            Analytics.trackRemoveFromCart(locale, {
                event: cartType === 'savedcart' ? 'remove_from_savedcart' : 'remove_from_cart',
                eventAction: offerId,
                eventLabel: sku,
                offerId,
                cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.total) || 0),
            });
        } catch (error) {
            setIsCartLoading(false);

            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const updateQuantity = async (offerId: string, quantity: number): Promise<void> => {
        try {
            const product = cart.items.find((item: TProductCart) => item.offerCode === offerId);
            const response = await Cart(locale.apiLocale).put(offerId, quantity);
            setCart(response.data);
            scroll.scrollToTop({ animated: true });
            const updatedCart = response && response.data;
            if (quantity > product.quantity) {
                Analytics.trackAddToCart(locale, {
                    event: 'add_to_cart',
                    eventAction: offerId,
                    eventLabel: product.skuConfig,
                    offerId,
                    quantity,
                    skuConfig: product.skuConfig,
                    cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                    cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.total) || 0),
                });
            } else {
                Analytics.trackRemoveFromCart(locale, {
                    event: 'remove_from_cart',
                    eventAction: offerId,
                    eventLabel: product.skuConfig,
                    offerId,
                    skuConfig: product.skuConfig,
                    cartItemsTotal: (updatedCart && updatedCart.cartCount) || 0,
                    cartValueTotal: toUSD(locale.isoLocale, (updatedCart && updatedCart.invoice.total) || 0),
                });
            }
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const setDestination = async (cityId: number): Promise<void> => {
        setIsCityLoading(true);
        try {
            const response = await Cart(locale.apiLocale).postCity(cityId, '&showCart');
            setCart(response.data);
            setIsCityLoading(false);
        } catch (error) {
            setCartError(error);
            setIsCityLoading(false);
            reportError(error, { componentStack: 'cartContext.getCart' });
        }
    };

    const getSelectedDestination = (): TCity => {
        let selectedDestination = {} as TCity;

        try {
            const { city } = webConfig.country;

            selectedDestination =
                city && city.filter((cityItem: TCity) => cityItem.id === (cart && cart.estimatedDeliveryCity))[0];
        } catch (error) {
            setCartError(error);
            reportError(error, { componentStack: 'cartContext.getSelectedDestination' });
        }

        return selectedDestination;
    };

    const isAdded = (offerCode: string): boolean => {
        return (
            !!cart &&
            !!cart.items.find(offer => {
                return 'offerCode' in offer && offer.offerCode === offerCode;
            })
        );
    };

    const getItemQty = (offerCode: string): number => {
        const currentOffer = !!cart && cart.items.find(offer => 'offerCode' in offer && offer.offerCode === offerCode);
        if (currentOffer) {
            return currentOffer.quantity;
        }
        return 0;
    };

    const getItemMaxQty = (offerCode: string): number => {
        const currentOffer = !!cart && cart.items.find(offer => 'offerCode' in offer && offer.offerCode === offerCode);
        if (currentOffer) {
            return currentOffer.quantityMax;
        }
        return 0;
    };

    const setFoupon = (offerCode: string, isFouponSelected): void => {
        const response = Cart(locale.apiLocale).setFoupon(offerCode, isFouponSelected);
        setCart({ ...cart, ...response } as TCart);
    };

    const cmsData: TLoyaltyCMSType = {};
    if (cart?.cms && cart.cms.data.length > 0) {
        cmsData.top = cart.cms.data.find(module => module.position === 'top');
        cmsData.midHigh = cart.cms.data.find(module => module.position === 'midHigh');
        cmsData.midLow = cart.cms.data.find(module => module.position === 'midLow');
        cmsData.bottom = cart.cms.data.find(module => module.position === 'bottom');
    }

    const cartContextValue = {
        // ------state--------
        cart,
        cartError,
        isAddToCartLoading,
        isCartLoading,
        isCartSucceeded,
        isQuickCartOpen,
        isCityLoading,
        isDiscountLoading,
        isAddonToCartLoading,
        cmsData,
        addonToCartState,
        addToCartState,
        // -----functions-----
        addAttachmentsToCart,
        addAddonsToCart,
        addToCart,
        addToWishlist,
        isInWishlist,
        applyCoupon,
        deleteCoupon,
        getCart,
        moveItem,
        removeItem,
        updateQuantity,
        setDestination,
        setIsCartSucceeded,
        getSelectedDestination,
        closeQuickCart,
        isAdded,
        getItemQty,
        getItemMaxQty,
        setFoupon,
        setAddonToCartState,
        setAddToCartState,
    };

    return <CartContext.Provider value={cartContextValue}>{children}</CartContext.Provider>;
};
